import React from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import {
  Dac7ReportResponse,
  GenerateDac7XmlResponse,
} from '../../../common/api/dac7Service';
import { LOADING_STATUS } from '../../../common/constants';

type GenerateXmlCardProps = {
  reports: Dac7ReportResponse[];
  generateReportId: string;
  docTypeIndic: string;
  generateXmlStatus: LOADING_STATUS;
  lastXmlResult: GenerateDac7XmlResponse | null;
  onReportChange: (value: string) => void;
  onDocTypeChange: (value: string) => void;
  onGenerate: () => void;
  onDismissLastXml: () => void;
  formatDate: (dateString: string) => string;
};

function GenerateXmlCard({
  reports,
  generateReportId,
  docTypeIndic,
  generateXmlStatus,
  lastXmlResult,
  onReportChange,
  onDocTypeChange,
  onGenerate,
  onDismissLastXml,
  formatDate,
}: GenerateXmlCardProps) {
  const selectedReport = reports.find(r => r.id === generateReportId);
  const isSubmitted =
    selectedReport?.status === 'SubmittedToIlmoitin' ||
    selectedReport?.status === 'Corrected';

  return (
    <Card className="bg-white border shadow-none p-24 mb-24">
      <h5 className="mb-16">Generoi DAC7 XML</h5>
      <p className="text-muted mb-16">
        Generoi DAC7 XML-tiedosto valitulle raportille. XML-tiedosto voidaan
        lähettää Ilmoitin-palveluun.
      </p>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-16">
            <Form.Label>Valitse raportti</Form.Label>
            <Form.Select
              value={generateReportId}
              onChange={e => onReportChange(e.target.value)}
              disabled={generateXmlStatus === LOADING_STATUS.LOADING}
            >
              <option value="">-- Valitse raportti --</option>
              {reports.map(report => (
                <option key={report.id} value={report.id}>
                  {report.reportingYear} - {report.messageRefId} (
                  {report.status})
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-16">
            <Form.Label>Dokumenttityyppi</Form.Label>
            <Form.Select
              value={docTypeIndic}
              onChange={e => onDocTypeChange(e.target.value)}
              disabled={generateXmlStatus === LOADING_STATUS.LOADING}
            >
              <option value="OECD1" disabled={isSubmitted}>
                OECD1 - Uusi ilmoitus
                {isSubmitted ? ' (estetty - raportti lähetetty)' : ''}
              </option>
              <option value="OECD2" disabled={!isSubmitted}>
                OECD2 - Korjaus
              </option>
              <option value="OECD3" disabled>
                OECD3 - Poisto (ei käytössä)
              </option>
            </Form.Select>
            {isSubmitted && (
              <Form.Text className="text-warning">
                Raportti on lähetetty. Vain korjaukset (OECD2) ja poistot
                (OECD3) sallittu.
              </Form.Text>
            )}
          </Form.Group>
        </Col>
        <Col md={4} className="d-flex align-items-end">
          <Button
            variant="primary"
            onClick={onGenerate}
            disabled={
              generateXmlStatus === LOADING_STATUS.LOADING || !generateReportId
            }
            className="mb-16 w-100"
          >
            {generateXmlStatus === LOADING_STATUS.LOADING ? (
              <>
                <Spinner size="sm" className="me-2" />
                Generoidaan...
              </>
            ) : (
              'Generoi ja lataa XML'
            )}
          </Button>
        </Col>
      </Row>

      {lastXmlResult && (
        <Row className="mt-16">
          <Col>
            <Alert variant="success" dismissible onClose={onDismissLastXml}>
              <Alert.Heading>XML generoitu onnistuneesti!</Alert.Heading>
              <ul className="mb-0">
                <li>Tiedosto: {lastXmlResult.fileName}</li>
                <li>Versio: {lastXmlResult.versionNumber}</li>
                <li>Myyjiä: {lastXmlResult.sellerCount}</li>
                <li>Kohteita: {lastXmlResult.propertyListingCount}</li>
                <li>Generoitu: {formatDate(lastXmlResult.generatedAt)}</li>
              </ul>
            </Alert>
          </Col>
        </Row>
      )}
    </Card>
  );
}

export default GenerateXmlCard;
